import { useEffect, useRef, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Button } from '@platform/ui'
import { Loader2, MailWarning, ShieldAlert, Sparkles } from 'lucide-react'
import { BrandLogo } from '@/modules/shared/BrandLogo'
import { useRouter, useSearchParams } from '@/lib/mocks/router'
import { canjearEnlace } from '@/lib/demo/enter-api'
import { sellarProspecto } from '@/lib/demo/lead-ref'
import { platform } from '@/lib/platform'

type Estado = 'canjeando' | 'listo' | 'invalido' | 'vencido' | 'sin-red'

/**
 * El auxilio para quien se quedó fuera.
 *
 * Aquí había un botón que devolvía al formulario público, y dejó de ser cierto:
 * el acceso se emite UNA vez por correo, así que volver a pedirlo desde ahí no
 * hace nada —contesta que sí y no manda nada, para no delatar quién está
 * registrado—. Ofrecerlo sería mandar a la persona a un callejón sin salida.
 *
 * Reemitir sí se puede, pero desde la bandeja comercial y con clave de
 * plataforma. Por eso la salida es escribirle a alguien.
 */
function Auxilio({ asunto }: { asunto: string }) {
  const correo = platform.brand?.supportEmail
  if (!correo) return null
  return (
    <Button asChild className="w-full">
      <a href={`mailto:${correo}?subject=${encodeURIComponent(asunto)}`}>Escribinos</a>
    </Button>
  )
}

/**
 * Puerta de entrada a la demo compartida — `…/enter?t=TOKEN`.
 *
 * Es el punto donde nace la correlación: se canjea el token contra el servidor,
 * sale el `leadId` del prospecto y queda sellado en este navegador. A partir de
 * ahí, cada acción que haga se atribuye a él, aunque la demo la estén usando
 * otras diez personas al mismo tiempo.
 *
 * El primer canje emite `demo_opened`; los siguientes emiten `return_visit`. Esa
 * decisión la toma el servidor, no esta pantalla: aquí no se sabe si es la
 * primera vez, y preguntarlo sería un viaje de más.
 */
export function Enter() {
  const router = useRouter()
  const params = useSearchParams()
  const token = params.get('t')
  const [estado, setEstado] = useState<Estado>('canjeando')
  const [nombre, setNombre] = useState<string | null>(null)
  // El canje tiene efectos —cuenta una visita y puntúa— así que corre UNA sola
  // vez aunque React vuelva a montar el efecto.
  const yaCorrio = useRef(false)

  useEffect(() => {
    if (yaCorrio.current) return
    yaCorrio.current = true

    if (!token) {
      setEstado('invalido')
      return
    }

    void canjearEnlace(token).then((r) => {
      if (r.ok) {
        sellarProspecto({ leadId: r.leadId, name: r.name ?? null, expiresAt: r.expiresAt ?? null })
        setNombre(r.name ?? null)
        setEstado('listo')
        // Se deja un instante para que se vea el saludo antes de entrar.
        window.setTimeout(() => router.push('/dashboard/user'), 1200)
        return
      }
      setEstado(r.reason === 'expired' ? 'vencido' : r.reason === 'network' ? 'sin-red' : 'invalido')
    })

    // SIN función de limpieza, y esto es la corrección de un fallo real: la
    // pantalla se quedaba clavada para siempre en "Preparando tu espacio".
    //
    // Había una bandera `cancelado` que la limpieza ponía en cierto, y la
    // promesa la miraba antes de escribir el estado. El problema es que
    // `useRouter()` devuelve un objeto NUEVO en cada renderizado, así que la
    // dependencia cambiaba siempre: React limpiaba el efecto en cada
    // renderizado, la bandera quedaba en cierto al instante, y cuando el canje
    // respondía —correctamente, con 201— su resultado se descartaba en
    // silencio. El guardián de arriba impedía reintentarlo. Congelada.
    //
    // El guardián ya garantiza una sola ejecución, así que la bandera no
    // aportaba nada. Y escribir estado tras desmontar no filtra memoria en
    // React 18: era una precaución copiada sin motivo que rompía el caso feliz.
    //
    // `router` sale de las dependencias por lo mismo. Se usa dentro pero es
    // estable en la práctica —navegar— y listarlo solo provoca reejecuciones.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md text-center space-y-6">
        <BrandLogo className="h-10 mx-auto" />

        {estado === 'canjeando' && (
          <>
            <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto" />
            <p className="text-muted-foreground">Preparando tu espacio…</p>
          </>
        )}

        {estado === 'listo' && (
          <>
            <Sparkles className="w-10 h-10 text-primary mx-auto" />
            <h1 className="text-2xl font-semibold">
              {nombre ? `Hola, ${nombre}` : 'Todo listo'}
            </h1>
            <p className="text-muted-foreground">
              Tu espacio en {platform.brand.productName} está preparado. Te llevamos adentro.
            </p>
          </>
        )}

        {estado === 'vencido' && (
          <>
            <MailWarning className="w-10 h-10 text-amber-500 mx-auto" />
            <h1 className="text-2xl font-semibold">Tu acceso venció</h1>
            <p className="text-muted-foreground">
              Los accesos duran siete días y el tuyo ya los cumplió. Escribinos y te lo
              reactivamos.
            </p>
            <Auxilio asunto="Se me venció el acceso a la demo" />
          </>
        )}

        {estado === 'invalido' && (
          <>
            <ShieldAlert className="w-10 h-10 text-destructive mx-auto" />
            <h1 className="text-2xl font-semibold">Este enlace no sirve</h1>
            <p className="text-muted-foreground">
              Puede que se haya reemplazado por uno más nuevo. Revisá el último correo que te
              enviamos; si tampoco funciona, escribinos.
            </p>
            <Auxilio asunto="Mi enlace de acceso a la demo no funciona" />
          </>
        )}

        {estado === 'sin-red' && (
          <>
            <ShieldAlert className="w-10 h-10 text-amber-500 mx-auto" />
            <h1 className="text-2xl font-semibold">No pudimos conectar</h1>
            <p className="text-muted-foreground">
              Probá de nuevo en un momento. Tu enlace sigue siendo válido.
            </p>
            <Button onClick={() => window.location.reload()} className="w-full">
              Reintentar
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

export const Route = createFileRoute('/enter')({
  component: Enter,
})
